// Social media icon row.
//
// Links come from settings/site and are edited in the admin panel, so this
// renders only the networks that actually have a URL saved — an empty field
// means the icon is not shown at all, rather than a dead link.
import { Facebook, Instagram } from "lucide-react";

/**
 * lucide-react has no TikTok glyph (it ships only openly-licensed marks), so
 * this is drawn inline. Sized and stroked to sit level with the lucide icons
 * beside it.
 */
function TikTok({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .53.04.77.12v-3.2a5.8 5.8 0 0 0-.77-.05A5.72 5.72 0 0 0 4.14 15.3 5.72 5.72 0 0 0 9.86 21a5.72 5.72 0 0 0 5.72-5.72V9.01a7.35 7.35 0 0 0 4.28 1.37V7.3a4.3 4.3 0 0 1-3.26-1.48z" />
    </svg>
  );
}

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTok,
};

/**
 * @param {Array<{key: string, label: string, url: string}>} socials from useSite()
 * @param {"dark"|"light"} tone  surface the icons sit on
 */
export default function SocialLinks({ socials = [], tone = "dark", className = "" }) {
  if (!socials.length) return null;

  const styles =
    tone === "dark"
      ? "border-white/15 bg-white/5 text-slate-300 hover:border-brand-light/50 hover:bg-brand/20 hover:text-white"
      : "border-slate-200 bg-white text-slate-500 hover:border-brand/40 hover:bg-brand/5 hover:text-brand-ink";

  return (
    <ul className={`flex flex-wrap items-center gap-2.5 ${className}`} data-testid="social-links">
      {socials.map(({ key, label, url }) => {
        const Icon = ICONS[key];
        if (!Icon) return null;
        return (
          <li key={key}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${label} (opens in a new tab)`}
              title={label}
              data-testid={`social-${key}`}
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300 ${styles}`}
            >
              <Icon className="h-4.5 w-4.5" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
